/**
 * Localisation mini-map for the voyage PDP.
 *
 * Lives inside the "Localisation et transport" accordion, which is collapsed by
 * default — a map has no size while its container is hidden, so init is deferred
 * until the accordion first opens (or immediately if it is already open). Reads
 * its coordinates from data attributes; MapLibre is loaded from the CDN by the
 * section, so we poll for the global before building.
 */
class VoyageMinimap extends HTMLElement {
  connectedCallback() {
    this.lat = parseFloat(this.dataset.lat);
    this.lng = parseFloat(this.dataset.lng);
    if (Number.isNaN(this.lat) || Number.isNaN(this.lng)) return;

    this.map = null;
    const details = this.closest('details');
    if (details && !details.open) {
      this.controller = new AbortController();
      details.addEventListener(
        'toggle',
        () => {
          if (details.open) this.#ensureMap();
        },
        { signal: this.controller.signal }
      );
    } else {
      this.#ensureMap();
    }
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.maplibreTimer) clearInterval(this.maplibreTimer);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  async #ensureMap() {
    if (this.map) {
      this.map.resize();
      return;
    }

    const maplibre = await this.#whenMaplibreReady();
    if (!maplibre || this.map) return;

    this.map = new maplibre.Map({
      container: this,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors',
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      },
      center: [this.lng, this.lat],
      zoom: 13,
      attributionControl: { compact: true },
    });

    const marker = document.createElement('span');
    marker.className = 'voyage-product__minimap-marker';
    if (this.dataset.label) marker.setAttribute('aria-label', this.dataset.label);
    new maplibre.Marker({ element: marker }).setLngLat([this.lng, this.lat]).addTo(this.map);

    // The accordion animates open, so the container may still be sizing on first paint.
    this.resizeTimeout = setTimeout(() => this.map && this.map.resize(), 350);
  }

  #whenMaplibreReady() {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    return new Promise((resolve) => {
      let waited = 0;
      this.maplibreTimer = setInterval(() => {
        waited += 50;
        if (window.maplibregl) {
          clearInterval(this.maplibreTimer);
          resolve(window.maplibregl);
        } else if (waited >= 5000) {
          clearInterval(this.maplibreTimer);
          resolve(null);
        }
      }, 50);
    });
  }
}

if (!customElements.get('voyage-minimap')) {
  customElements.define('voyage-minimap', VoyageMinimap);
}
